'use client';

import { useState, useEffect } from 'react';
import { Smartphone, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/utils/api';
import { resolveApkDownloadFromActiveResponse } from '@/utils/app-apk-download';

const isDesktop = () => {
    if (typeof window === 'undefined') return true;
    const ua = navigator.userAgent;
    if (/Windows NT|Linux/.test(ua) && !/Android/.test(ua)) return true;
    if (/Macintosh/.test(ua) && !/iPad|iPhone|iPod/.test(ua)) {
        return true;
    }
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && window.screen.width >= 1280) return true;
    return false;
};

const isIOS = () => {
    if (typeof window === 'undefined') return false;
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && window.screen.width < 1280;
};

const isAndroid = () => {
    if (typeof window === 'undefined') return false;
    return /Android/.test(navigator.userAgent);
};

export default function HomeAppButton() {
    const [apkUrl, setApkUrl] = useState<string | null>(null);
    const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
    const [iosExpanded, setIosExpanded] = useState(false);

    useEffect(() => {
        if (isDesktop()) return;
        if (isIOS()) setPlatform('ios');
        else if (isAndroid()) setPlatform('android');
    }, []);

    useEffect(() => {
        const fetchApk = async () => {
            try {
                const { data } = await api.get('app-version/active');
                const url = resolveApkDownloadFromActiveResponse(data);
                if (url) setApkUrl(url);
            } catch (error) {
                console.error('Erro ao buscar APK:', error);
            }
        };
        fetchApk();
    }, []);

    if (!platform) return null;

    return (
        <div className="mt-6 mb-8 rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto justify-center sm:justify-start">
                    <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                        <Smartphone size={18} className="text-orange-500" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-white/90">
                            {platform === 'ios' ? 'Instalar Web App' : 'App Oficial da Mediocre'}
                        </span>
                        <span className="text-xs text-white/50">
                            {platform === 'ios' ? 'Adicione à tela inicial para melhor experiência' : 'Leia com mais conforto e receba notificações'}
                        </span>
                    </div>
                </div>

                {platform === 'android' ? (
                    <a
                        href={apkUrl ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => { if (!apkUrl) e.preventDefault(); }}
                        title={apkUrl ? 'Baixar APK' : 'APK indisponível'}
                        className={`w-full sm:w-auto justify-center shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-base font-semibold transition-colors ${apkUrl ? 'bg-orange-500 hover:bg-orange-400 text-white' : 'bg-white/10 text-white/40 cursor-not-allowed'}`}
                    >
                        <Download size={20} />
                        <span className="hidden sm:inline">Baixar aplicativo oficial da Mediocre</span>
                        <span className="sm:hidden">Baixar App</span>
                    </a>
                ) : (
                    <button
                        type="button"
                        onClick={() => setIosExpanded((v) => !v)}
                        className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
                        aria-expanded={iosExpanded}
                    >
                        {iosExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        <span className="text-sm">Como?</span>
                    </button>
                )}
            </div>

            {platform === 'ios' && iosExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-white/5 mt-0">
                    <p className="text-xs text-white/50 leading-relaxed">
                        Toque em <span className="text-white/70">Compartilhar</span> (ícone □↑) na barra do Safari e escolha <span className="text-white/70">Adicionar à Tela Inicial</span>.
                    </p>
                </div>
            )}
        </div>
    );
}
